import { Group, Stack, TextInput } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { Table, TableColumn } from '../../../components/table/table';
import { skyDark } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { TaskRecord } from '../../tasks-table/tasks-table.type';

interface AgenticProcessTableProps {
  tasks: TaskRecord[];
  columns: TableColumn<TaskRecord>[];
}

export const AgenticProcessTable = ({ tasks, columns }: AgenticProcessTableProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const tasksStringLower = t('common.work_units').toLowerCase();

  const filteredTasks = useMemo(() => {
    if (!searchTerm.trim()) return tasks;

    const searchLower = searchTerm.toLowerCase();
    return tasks.filter((task) => {
      const name = (task.name ?? '').toLowerCase();
      const title = (task.title ?? '').toLowerCase();
      const type = (task.type ?? '').toLowerCase();
      return name.includes(searchLower) || title.includes(searchLower) || type.includes(searchLower);
    });
  }, [tasks, searchTerm]);

  if (tasks.length === 0) {
    return <ContentPlaceholder icon="assignment" text={`No ${tasksStringLower} found for the selected time period.`} />;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text weight="bold" size="large">
          {`${t('common.work_units')} Using Agents`}
        </Text>
        <TextInput
          placeholder={`Search ${tasksStringLower}...`}
          radius="xl"
          size="sm"
          value={searchTerm}
          leftSection={<Icon name="search" variant="filled" size={16} color={skyDark} />}
          style={{ width: 300 }}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
        />
      </Group>

      {filteredTasks.length === 0 && searchTerm.trim() ? (
        <ContentPlaceholder icon="search" text={`No ${tasksStringLower} found matching your search criteria.`} />
      ) : (
        <Table data={filteredTasks} columns={columns} getRowId={(row) => row.id} stickyHeader={true} />
      )}
    </Stack>
  );
};
